import createMeridianDefinitionTokens from '@amzn/meridian-tokens/theme/definition';
import { accentHoverColor, accentPressedColor } from './accent';
import { withAlpha } from './color';
import fontStacks from './font-stacks';
import meridianOrangePalette from './meridian-orange';
import { motionDurations, motionEasing } from './motion';

export const sidebarBackgrounds = {
  light: 'linear-gradient(155deg, #fffaf8 0%, #fff6f2 48%, #fff0ea 100%)',
  dark: 'linear-gradient(155deg, #16100e 0%, #1b120f 48%, #241410 100%)',
} as const;

// Meridian only documents palette theming; these font tokens are overridden so Inter replaces
// Amazon Ember everywhere Meridian renders text, including components with their own font token.
const fontFamilyTokens = {
  textDefaultFontFamily: fontStacks.sans,
  textFontFamilyAmazonEmber: fontStacks.sans,
  textFontFamilyAmazonEmberMonospace: fontStacks.monospace,
  badgeFontFamily: fontStacks.sans,
  bottomNavigationFontFamily: fontStacks.sans,
  chartLabelFontFamily: fontStacks.sans,
  gaugeLabelFontFamily: fontStacks.sans,
  gaugeTickMarkLabelFontFamily: fontStacks.sans,
  pieLabelFontFamily: fontStacks.sans,
  trendTextFontFamily: fontStacks.sans,
} as const;

const meridianDefaultTokens = createMeridianDefinitionTokens(meridianOrangePalette);

const motionTokens = {
  buttonMotionDuration: motionDurations.instant,
  buttonMotionFunction: motionEasing.standard,
  expanderContentMotionDelay: '0ms',
  expanderContentMotionDuration: motionDurations.moderate,
  expanderContentMotionFunction: motionEasing.standard,
  expanderIconMotionDelay: '0ms',
  expanderIconMotionDuration: motionDurations.moderate,
  expanderIconMotionFunction: motionEasing.standard,
  inputLabelMotionFunction: motionEasing.standard,
  linkMotionDuration: motionDurations.instant,
  linkMotionFunction: motionEasing.standard,
  modalMotionDuration: motionDurations.moderate,
  modalMotionFunction: motionEasing.enter,
  popoverMotionDuration: motionDurations.quick,
  popoverMotionFunction: motionEasing.enter,
  scrimMotionFunction: motionEasing.standard,
  sheetMotionFunction: motionEasing.enter,
  sheetMotionTiming: motionDurations.moderate,
  sideMenuMotionExpandDuration: motionDurations.moderate,
  sideMenuMotionExpandFunction: motionEasing.standard,
  sideMenuMotionInteractDuration: motionDurations.instant,
  sideMenuMotionInteractFunction: motionEasing.standard,
  tabMotionFunction: motionEasing.standard,
  tableMotionFunction: motionEasing.standard,
  toggleMotionFunction: motionEasing.standard,
} as const;

export default function createMeridianThemeTokens(isDarkMode: boolean) {
  const primaryActionColor = isDarkMode ? meridianOrangePalette.palette300 : meridianOrangePalette.palette500;
  const primaryActionPressedColor = isDarkMode ? meridianOrangePalette.palette100 : meridianOrangePalette.palette700;
  // Deliberately one step lighter than Meridian's own mapping: its "Selected" neutral is the faintest
  // tint (light #E9EBEF) and its "Hover" neutral the next step (#CCD5DE), so hover stays subtle and
  // pressed is the stronger state.
  const neutralHoverColor = isDarkMode
    ? meridianDefaultTokens.themeActionNeutralSelectedDark
    : meridianDefaultTokens.themeActionNeutralSelectedLight;
  const neutralPressedColor = isDarkMode
    ? meridianDefaultTokens.themeActionNeutralHoverDark
    : meridianDefaultTokens.themeActionNeutralHoverLight;
  const subduedBorderColor = isDarkMode
    ? meridianDefaultTokens.themeBorderSubduedDark
    : meridianDefaultTokens.themeBorderSubduedLight;

  return {
    ...fontFamilyTokens,
    ...motionTokens,
    appSidebarBackground: isDarkMode ? sidebarBackgrounds.dark : sidebarBackgrounds.light,
    alertBorderRadius: 8,
    boxBorderRadiusFill: 8,
    boxBorderRadiusOutline: 8,
    buttonBackgroundColorTertiaryHover: neutralHoverColor,
    buttonBackgroundColorTertiaryPressed: neutralPressedColor,
    buttonBorderColorTertiaryHover: primaryActionColor,
    buttonBorderColorTertiaryPressed: primaryActionPressedColor,
    buttonBorderRadius: 8,
    buttonBorderWidth: 1,
    buttonForegroundColorTertiaryFocus: primaryActionColor,
    buttonForegroundColorTertiaryHover: primaryActionColor,
    buttonForegroundColorTertiaryPressed: primaryActionPressedColor,
    cardBorderColor: subduedBorderColor,
    cardBorderRadius: 12,
    cardDropShadowDefaultBlur: 2,
    cardDropShadowDefaultOffsetY: 1,
    cardDropShadowDefaultOpacity: isDarkMode ? 30 : 4,
    cardDropShadowDefaultSpread: 0,
    dataTableBorderTableRadius: 8,
    focusRingDropShadowBlur: 1,
    focusRingDropShadowOpacity: 8,
    focusRingWidth: 1,
    inputBorderRadius: 8,
    inputEndcapBorderRadius: 8,
    inputFocusRingBorderRadius: 8,
    modalBorderRadius: 16,
    modalDropShadowBlur: 48,
    modalDropShadowOffsetY: 24,
    modalDropShadowOpacity: isDarkMode ? 60 : 24,
    modalDropShadowSpread: -12,
    modalFooterBorderWidth: 0,
    // Footer and close button share the modal body's 24px inset so buttons and the X line up with content.
    modalCloseButtonSpacingWebEnd: 16,
    modalCloseButtonSpacingWebTop: 16,
    modalFooterSpacingInset: 24,
    popoverBorderRadius: 12,
    scrimBackgroundOpacity: 40,
    searchFieldSuggestionListBorderRadius: 12,
    sheetShadowBlur: 28,
    sheetShadowOpacity: isDarkMode ? 20 : 8,
    sheetShadowSpread: -2,
    sideMenuBorderColor: subduedBorderColor,
    sideMenuLinkBackgroundColorHover: accentHoverColor(isDarkMode),
    sideMenuLinkBackgroundColorPressed: accentPressedColor(isDarkMode),
    sideMenuLinkBackgroundColorSelected: isDarkMode
      ? withAlpha(meridianOrangePalette.palette300, 0.16)
      : meridianOrangePalette.palette50,
    sideMenuLinkFontSize: 14,
    sideMenuLinkFontWeight: 500,
    sideMenuLinkIndicatorWidth: 3,
    sideMenuLinkLineHeight: '20px',
    sideMenuLinkSpacingInsetVertical: 10,
    stackedTableBorderRadius: 8,
    tabBorderRadius: 8,
    textH200FontSize: 16,
    textH200LineHeight: '20px',
    textH300FontSize: 18,
    textH300LineHeight: '24px',
    textH400FontSize: 20,
    textH400LineHeight: '28px',
    textH500FontSize: 24,
    textH500LineHeight: '32px',
    textH600FontSize: 28,
    textH600LineHeight: '36px',
  } as const;
}
